import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useCallback, useState } from "react";
import { createRoot } from "react-dom/client";
import { useQuery, useQueryClient } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { updateCustomCladColorPrice } from "../../api/OrderSummaryApi";
import {
  getIgnoreCartCheck,
  getIgnoreUnitCheck,
  getQuote,
  getUser,
  postAdminFactor,
  postDuplicate,
  postIgnoreCartCheck,
  postQtyUpdate,
  postUnit,
  postUnitUpdate,
} from "../../api/SidebarApi";
import { UserObj } from "../../GlobalTypes";
import usePersistedState from "../../hooks/usePersistedState";
import { useAccordion } from "../../store/accordion";
import { useStore } from "../../store/store";
import checkCart, { LineItem } from "../../util/checkCart";
import checkForCommonHardware, {
  FixesObj,
} from "../../util/checkForCommonHardware";
import dealerRoles from "../../util/dealerAccounts";
import { UnitObj } from "../../util/quoteCartStorage";
import sortUnits from "../../util/sortUnits";
import CanvasBox from "../Canvas/CanvasBox";

function getSidebarTotals(sidebarArg: {
  isFetching: boolean;
  fetchingUser: boolean;
  userData: UserObj;
  adminChargeLabel: string;
  adminCharge: number;
  hasFreightQuote: boolean;
}) {
  const {
    isFetching,
    fetchingUser,
    userData,
    adminChargeLabel,
    adminCharge,
    hasFreightQuote,
  } = sidebarArg;

  const labels = [
    "Total",
    "Custom Clad Color: ",
    "Split Finish: ",
    "Delivery Charge: ",
    "Dealer Net Cost: ",
  ];

  if (isFetching || fetchingUser) {
    return labels;
  }

  if (userData && userData.dealerTax) {
    if (adminCharge) {
      labels.splice(3, 0, `${adminChargeLabel || "Admin Charge"}: `);
      labels.splice(4, 0, "Sales Tax: ");
    } else {
      labels.splice(3, 0, "Sales Tax: ");
    }
  }

  if (hasFreightQuote) {
    const i = labels.findIndex((itm) => itm === "Delivery Charge: ");
    labels.splice(i, 1, "Freight: ");
    labels.splice(i + 1, 0, "Crate: ");
  }

  return labels;
}

function useSidebarSelector() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = location.state;

  const modalMessage =
    "An Error Occurred for this Unit. Please Contact Customer Service at (805)644-4494 for Technical Assistance.";

  const quoteNumber = JSON.parse(
    sessionStorage.getItem("quoteNumber") || JSON.stringify(""),
  );

  const theme = useTheme();

  const [fetch, setFetch] = useState(false);

  const queryClient = useQueryClient();

  const signedInUserRole = JSON.parse(
    sessionStorage.getItem("role") || JSON.stringify(""),
  );

  const signedInUserEmail = JSON.parse(
    sessionStorage.getItem("email") || JSON.stringify(""),
  );

  const { data: userData, isFetching: fetchingUser } = useQuery(
    "Dealer",
    async () => {
      const response = await getUser(userId);
      if (response.data.signInRole === "Production") {
        const user = JSON.parse(
          sessionStorage.getItem("user") || JSON.stringify(""),
        );
        navigate(`/dealerZone/${user}`);
        return;
      }
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      retry: 2,
      onSuccess: () => setFetch(true),
    },
  );

  const { data, isFetching } = useQuery(
    (fetch || userData) && "QuoteCart",
    async () => {
      const response = await getQuote(userId, quoteNumber);

      const data = response.data;

      const { quote, expiredUpdate, disableQSum } = data;

      if (quote.status !== "Quote" && quote.status !== "Archived") {
        if (
          dealerRoles.includes(userData.signInRole) ||
          userData.signInRole === "Outside Sales"
        ) {
          return navigate("/logout");
        }
      }

      expiredUpdate && handleOpen(expiredUpdate);

      quote.factor && setAdminFactor(parseFloat(quote.factor));

      if (quote.units.length) {
        setOrder(sortUnits(quote.units) || []);
      }

      // setCustomCladColorPrice(quote.customCladPrice);

      setLoadingQU(false);

      return { quote, expiredUpdate, disableQSum };
    },
    { refetchOnWindowFocus: false, retry: 2 },
  );
  
  const [orderedUnits, setOrder] = useState<UnitObj[]>([]);

  const getStore = useStore((state) => state.getStore);
  const doorType = useStore((state) => state.quote.doorType);

  const addToQuote = useStore((state) => state.addToQuote);

  const isEdit = useStore((state) => state.isEdit);

  const resetQuote = useStore((state) => state.resetQuote);

  const handleMinimize = useAccordion((state) => state.handleMinimize);

  const [mobileOpen, setMobileOpen] = useState(false);

  const [editQuote, setEditQuote] = useState(false);
  const [isSubmitBtnPressed, setIsSubmitBtnPressed] = usePersistedState(
    "isSubmitBtnPressed",
    false,
  );

  const [nonMatches, setNonMatches] = useState<FixesObj[]>([]);

  const [openCommon, setOpenCommon] = useState(false); //used for unit check modal

  const handleCloseCommon = () => {
    setOpenCommon(false);
    setNonMatches([]);
  }; //used for unit check modal

  const [cartCheck, setCartCheck] = useState<LineItem[]>([]);

  const [openCCM, setOpenCCM] = useState(false); //used for cart check modal

  const handleCloseCCM = () => {
    setOpenCCM(false);
    setCartCheck([]);
  }; //used for cart check modal

  const [modalText, setModalText] = useState("");

  const [open, setOpen] = useState(false); //used for expired price modal

  const handleClose = () => setOpen(false); //used for expired price modal

  const handleOpen = (text: string) => {
    setModalText(text);
    setOpen(true);
  };

  const [loadingQuantity, setLoadingQU] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [adminFactor, setAdminFactor] = usePersistedState(
    "adminFactor",
    "None",
  );

  // const [windowVal, setWindowVal] = usePersistedState(
  //   "windowVal",
  //   "Casement Window"
  // );

  const handleCanvasImg = async () => {
    const hiddenDiv = document.createElement("div");
    hiddenDiv.style.display = "none"; // Ensure it's hidden
    document.body.appendChild(hiddenDiv);

    const root = createRoot(hiddenDiv);

    root.render(
      <CanvasBox
        canvasHeight={800}
        canvasWidth={800}
        setSize={() => ""}
        dataUrl={true}
      />,
    );

    // Wait for the canvas element to render (optional, depends on how CanvasBox works)
    await new Promise((resolve) => setTimeout(resolve, 200)); // Adjust timing if needed

    const canvasElem = hiddenDiv.querySelector("canvas");
    if (!canvasElem) {
      addToQuote({ elevationImg: "" });
      //clean up
      root.unmount();
      hiddenDiv.remove();
      return;
    }

    const canvasUrl = canvasElem.toDataURL("image/png", 1);

    addToQuote({ elevationImg: canvasUrl });

    // Clean up
    root.unmount();
    hiddenDiv.remove();
  };

  const addDoorToSidebar = async (
    withCheck?: boolean,
    ignoreUnitCheck?: boolean,
  ) => {
    if (
      (userData.role === "Inside Sales" ||
        userData.role === "Outside Sales" ||
        userData.role === "Admin") &&
      adminFactor === "None"
    ) {
      return alert("Select a Factor to Continue");
    }

    if (data?.quote.units.length === 35) {
      return handleOpen("Can not exceed 35 items in your cart");
    }

    const unit = getStore();

    if (isEdit && withCheck) {
      const unitId = JSON.parse(
        sessionStorage.getItem("unitId") || JSON.stringify(""),
      );
      const response = await getIgnoreUnitCheck(data?.quote._id, unitId);

      if (!response.data) {
        //unit check is for suggestions from millie
        const badMatch = checkForCommonHardware(unit);

        if (badMatch.length) {
          setNonMatches(badMatch);
          return setOpenCommon(true);
        }
      }
    } else if (!isEdit && withCheck) {
      const badMatch = checkForCommonHardware(unit);
      if (badMatch.length) {
        setNonMatches(badMatch);
        return setOpenCommon(true);
      }
    }

    if (
      unit.material === "All Aluminum" ||
      unit.doorType === "Bi-fold Door/Window Combo"
    ) {
      await handleCanvasImg();
    }

    setOpenCommon(false);
    setNonMatches([]);
    setIsLoading(true);

    unitCalculation(ignoreUnitCheck);
  };

  const unitCalculation = async (ignoreUnitCheck?: boolean) => {
    const store = getStore();
    try {
      if (isEdit) {
        editUnit(store, ignoreUnitCheck);
      } else {
        let promises: Promise<void>[] & void[] = [];

        promises.push(
          addToQuoteHistory(store, data?.quote._id, ignoreUnitCheck),
        );

        promises.push(resetAllValues());

        //fulfilled if  every promise in the array succeeds
        //allowss tasks to run concurrently
        Promise.all(promises).then(() => {
          setIsLoading(false);
          setWindowOrDoor("Bi-Fold");
          // setWindowVal("Casement Window");
        });
      }
    } catch (err) {
      handleOpen(modalMessage);
      return setIsLoading(false);
    }
  };

  const addToQuoteHistory = async (
    saved: UnitObj,
    id: string,
    ignoreUnitCheck?: boolean,
  ) => {
    localStorage.removeItem("accordionControl");
    localStorage.removeItem("requiredMsgs");
    !saved.doorType.includes("Bi-fold") &&
      localStorage.removeItem("displayedConfigOptions");
    const localStorageState = JSON.stringify(localStorage);

    try {
      const post = await postUnit({
        saved,
        id,
        localStorageState,
        ignoreUnitCheck,
        units: data?.quote.units,
      });

      if (post.data.quote === "Unit Added") {
        return queryClient.invalidateQueries("QuoteCart");
      }
    } catch (err) {
      console.log(err);
      handleOpen(modalMessage);
      setIsLoading(false);
    }
  };

  const duplicateUnit = async (unitId: string) => {
    if (data?.quote.units.length === 35) {
      return handleOpen("Can not exceed 35 items in your cart");
    }

    setLD(true);

    const response = await postDuplicate({
      unitId,
      id: data?.quote._id,
      orderIndex: data?.quote.units.length || 0,
    });

    if (response.data.error) {
      handleOpen(modalMessage);
      return setLD(false);
    }

    setLD(false);
    return queryClient.invalidateQueries("QuoteCart");
  };

  const editUnit = async (saved: UnitObj, ignoreUnitCheck?: boolean) => {
    if (isEdit) {
      const unitId = JSON.parse(
        sessionStorage.getItem("unitId") || JSON.stringify(""),
      );
      const localStorageState = JSON.stringify(localStorage);

      const orderIndex = orderedUnits.findIndex(
        (item) => item._id && item._id.toString() === unitId,
      );

      try {
        const response = await postUnitUpdate({
          saved,
          quoteId: data?.quote._id,
          unitId,
          localStorageState,
          orderIndex,
          ignoreUnitCheck,
        });

        if (response.data.error) {
          handleOpen(modalMessage);
          return setIsLoading(false);
        }

        queryClient.invalidateQueries("QuoteCart");
        resetAllValues();

        setIsLoading(false);
        setWindowOrDoor("Bi-Fold");
      } catch (err) {
        console.log(err);
        handleOpen(modalMessage);
        setIsLoading(false);
      }
    }
  };

  const resetAllValues = useCallback(() => {
    localStorage.clear();

    setIsSubmitBtnPressed(true);

    setTimeout(() => {
      setIsSubmitBtnPressed(false);
    }, 1000);

    resetQuote();
    handleMinimize();
  }, []);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const backToFormPage = () => {
    navigate(`/form/${userData.id}`, {
      state: { isQuoteCreated: true, userId },
    });
  };

  const handleResumeLater = () => {
    const loggedInUser = JSON.parse(
      sessionStorage.getItem("user") || JSON.stringify(""),
    );
    resetAllValues();
    navigate(`/dealerZone/${loggedInUser}`);
  };

  const changeQuantity = useCallback(
    async (unitId: string, newQuantity: string | number) => {
      setLoadingQU(true);

      const response = await postQtyUpdate(
        data?.quote._id,
        unitId,
        Number(newQuantity),
      );

      if (response.data.error) {
        handleOpen(modalMessage);
        return setLoadingQU(false);
      }

      return queryClient.invalidateQueries("QuoteCart");
    },
    [adminFactor, userData, data],
  );

  const handleAdminFactor = useCallback(
    async (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = e.target;
      if (data?.quote.status !== "Quote" && data?.quote.status !== "Archived") {
        return alert("Can not change factor on Order");
      }
      setAdminFactor(value);

      if (value === "None") return;

      if (!data?.quote.units.length) {
        const res = await postAdminFactor(data?.quote._id, value);
        if (res.data.error) handleOpen("Could not add factor");
        return;
      }

      setLoadingQU(true);

      const res = await postAdminFactor(data?.quote._id, value);

      if (res.data.error) {
        return handleOpen("An Error Occurred for this Unit.");
      }

      return queryClient.invalidateQueries("QuoteCart");
    },
    [data],
  );

  const routeToOrderSummary = useCallback(
    async (route: boolean, ignoreCheck?: boolean) => {
      if (
        userData.factor === 1 &&
        (!adminFactor || adminFactor === "None") &&
        (userData.role === "Admin" ||
          userData.role === "Inside Sales" ||
          userData.role === "Outside Sales") &&
        !data?.quote.factor
      ) {
        return handleOpen("Enter a Factor to Continue");
      }

      function changePage() {
        navigate(`/orderSummary/${userData.id}`, {
          state: {
            isQuoteCreated: true,
            region: userData.region,
            userId,
          },
        });
      }

      if (data?.quote.status !== "Quote" && data?.quote.status !== "Archived") {
        return changePage();
      }

      const response = await getIgnoreCartCheck(data?.quote._id);

      if (route || response.data) {
        if (ignoreCheck) {
          await postIgnoreCartCheck(data?.quote._id);
        }
        return changePage();
      }

      const check = checkCart(orderedUnits);

      if (check?.length && Object.keys(check[0]).length > 10) {
        setCartCheck(check);
        setOpenCCM(true);
      } else {
        changePage();
      }
    },
    [userData, data, orderedUnits],
  );

  const [checkedPD, setCheckedPD] = usePersistedState("checkedPD", false);

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setCheckedPD(event.target.checked);

  const [windowOrDoor, setWindowOrDoor] = usePersistedState(
    "windowOrDoor",
    "Bi-Fold",
  );

  const [loadDuplicate, setLD] = useState(false);

  const isDrawerCollapsed = useMediaQuery("(max-width:1300px)");

  const sidebarTotals = getSidebarTotals({
    isFetching,
    fetchingUser,
    userData,
    adminChargeLabel: data?.quote?.adminChargeLabel || "",
    adminCharge: data?.quote?.adminCharge || 0,
    hasFreightQuote:
      data?.quote?.freightQuote && data.quote.state !== "CA" ? true : false,
  });

  const canEditCustomCladColorAndFreightPrice =
    signedInUserRole === "Admin" ||
    signedInUserEmail === "laura@agmillworks.com";

  return {
    sidebarTotals,
    loadDuplicate,
    windowOrDoor,
    handleSwitchChange,
    checkedPD,
    routeToOrderSummary,
    handleAdminFactor,
    changeQuantity,
    handleResumeLater,
    backToFormPage,
    handleDrawerToggle,
    resetAllValues,
    duplicateUnit,
    addDoorToSidebar,
    adminFactor,
    isLoading,
    loadingQuantity,
    handleClose,
    open,
    modalText,
    handleCloseCCM,
    handleCloseCommon,
    openCCM,
    cartCheck,
    openCommon,
    isSubmitBtnPressed,
    editQuote,
    setEditQuote,
    nonMatches,
    data,
    isFetching,
    userData,
    fetchingUser,
    theme,
    orderedUnits,
    setOrder,
    setWindowOrDoor,
    isEdit,
    mobileOpen,
    doorType,
    userId,
    isDrawerCollapsed,
    canEditCustomCladColorAndFreightPrice,
  };
}

export default useSidebarSelector;
